import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { cookieBodyBounds } from "../utils/cookieBodyBounds";

const BUILD_PLATE = { x: 256, y: 256 };

/* 갤러리는 어두운 브라운 배경 그대로, 견적 화면은 썸네일과 같은 밝은 회색을 쓴다
   (2026-09-08 김현비). 기본값을 유지해 다른 화면에는 영향이 없다. */
export default function STLViewer({
  url,
  fileName: fileNameProp,
  onThumbnail,
  hideSize,
  bgColor = 0x4d3322,
  modelColor = 0xF0E8DC,
  gridColor = 0xc9935a,
  onBounds,
  // Gallery measurements exclude separate handles; rendering/export stay whole.
  bodyBoundsOnly = false,
  /* 카메라 거리 = 모델 크기 × zoom. 작을수록 크게 보인다 (갤러리는 1.55 로 확대, 2026-10-16) */
  zoom = 2.5,
  /* 선택: 크기 글자 모양을 바꾼다 ({x,y,z} → 문자열). 없으면 "X × Y × Z mm" (2026-10-16) */
  formatSize,
}) {
  const canvasWrapRef = useRef(null);
  const capturedRef = useRef(false);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const meshRef = useRef(null);
  const [modelSize, setModelSize] = useState(null);
  const [rotateStep, setRotateStep] = useState(90);
  /* 선택: 불러온 모델 크기(mm)를 부모에 알려준다 — 갤러리 상세 "크기" 줄용 (2026-10-16).
     ref 로 들고 있어 콜백이 바뀌어도 모델을 다시 불러오지 않는다. */
  const onBoundsRef = useRef(onBounds);
  onBoundsRef.current = onBounds;

  // 모델 방향 회전 (지오메트리 자체를 회전 후 재배치)
  const rotateModel = useCallback((axis, dir) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const angle = (dir * rotateStep * Math.PI) / 180;
    const matrix = new THREE.Matrix4();
    if (axis === "x") matrix.makeRotationX(angle);
    if (axis === "y") matrix.makeRotationY(angle);
    if (axis === "z") matrix.makeRotationZ(angle);

    mesh.geometry.applyMatrix4(matrix);
    mesh.geometry.computeBoundingBox();
    const box = mesh.geometry.boundingBox;
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    // 모델을 빌드 플레이트 중앙, 바닥에 배치
    mesh.position.set(-center.x, -box.min.y, -center.z);
    setModelSize({ x: size.x, y: size.y, z: size.z });
  }, [rotateStep]);

  useEffect(() => {
    if (!url) return;

    const mount = canvasWrapRef.current;
    if (!mount) return;

    /* Renderer */
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(bgColor, 1);
    mount.appendChild(renderer.domElement);

    /* Scene */
    const scene = new THREE.Scene();

    /* Camera */
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 5000);
    cameraRef.current = camera;

    /* Controls */
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controlsRef.current = controls;

    /* Lights - 입체 음영 강조 */
    scene.add(new THREE.AmbientLight(0xffffff, 0.3));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(200, 300, 200);
    scene.add(dirLight);

    /* Build plate grid - 제거됨 (깔끔한 뷰) */
    // scene.add(buildGrid(BUILD_PLATE, gridColor));

    /* Resize */
    const resize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      if (!width || !height) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };
    resize();
    window.addEventListener("resize", resize);

    /* STL Load */
    const loader = new STLLoader();
    loader.load(url, (geometry) => {
      // 넓고 평평한 면을 바닥으로 자동 배치
      autoOrient(geometry);

      // 내보낸 STL이 이미 화면 방향(+X 오른쪽 / +Y 위)으로 회전되어 나오므로
      // 여기서 추가 회전하면 톱뷰가 180도 뒤집힌다. (Cutter.js exportSTL 참고)

      geometry.computeBoundingBox();
      geometry.center();
      geometry.computeBoundingBox();
      const box = geometry.boundingBox;
      const size = new THREE.Vector3();
      box.getSize(size);

      setModelSize({ x: size.x, y: size.y, z: size.z });
      if (onBoundsRef.current) onBoundsRef.current(bodyBoundsOnly
        ? cookieBodyBounds(geometry.attributes.position.array)
        : { x: size.x, y: size.y, z: size.z });

      const material = new THREE.MeshStandardMaterial({
        color: modelColor,
        roughness: 0.5,
        metalness: 0.1,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(-box.getCenter(new THREE.Vector3()).x, -box.min.y, -box.getCenter(new THREE.Vector3()).z);
      meshRef.current = mesh;
      scene.add(mesh);

      // 카메라 위치 (살짝 기울인 톱뷰 - 그림자/입체감 표현)
      const modelDim = Math.max(size.x, size.z);
      const dist = modelDim * zoom;
      camera.position.set(0, dist * 0.9, dist * 0.4);
      controls.target.set(0, 0, 0);
      controls.update();

      renderer.render(scene, camera);

      requestAnimationFrame(() => {
        if (!capturedRef.current && onThumbnail) {
          const dataUrl = renderer.domElement.toDataURL("image/png");
          capturedRef.current = true;
          onThumbnail(dataUrl);
        }
      });
    });

    /* Animate */
    let animFrame;
    const animate = () => {
      animFrame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    const start = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      if (!w || !h) { requestAnimationFrame(start); return; }
      resize();
      animate();
    };
    start();

    /* Cleanup */
    return () => {
      window.removeEventListener("resize", resize);
      if (animFrame) cancelAnimationFrame(animFrame);
      controls.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      capturedRef.current = false;
      meshRef.current = null;
    };
  }, [url, onThumbnail, bgColor, modelColor, gridColor, zoom, bodyBoundsOnly]);

  const sizeText = modelSize
    ? (formatSize ? formatSize(modelSize) : `${modelSize.x.toFixed(1)} × ${modelSize.z.toFixed(1)} × ${modelSize.y.toFixed(1)} mm`)
    : null;

  return (
    <div className="gcode-viewer-wrap">
      <div className="gcode-viewport">
        {/* Three.js canvas container - absolute fill */}
        <div ref={canvasWrapRef} className="gcode-canvas" />

        {/* Bottom info */}
        <div className="gcode-bottom-info">
          {fileNameProp && <span className="gcode-filename">{fileNameProp}</span>}
          {sizeText && !hideSize && <span className="gcode-size">{sizeText}</span>}
        </div>
      </div>
    </div>
  );
}

/** Build plate grid */
function buildGrid({ x: plateX, y: plateY }, gridColor = 0xc9935a) {
  const group = new THREE.Group();
  const halfX = plateX / 2;
  const halfY = plateY / 2;
  const step = 10;

  const gridMat = new THREE.LineBasicMaterial({
    color: gridColor,
    transparent: true,
    opacity: 0.3,
    linewidth: 1,
  });
  const gridPositions = [];
  for (let i = -halfX; i <= halfX; i += step) gridPositions.push(i, 0, -halfY, i, 0, halfY);
  for (let j = -halfY; j <= halfY; j += step) gridPositions.push(-halfX, 0, j, halfX, 0, j);
  const gridGeom = new THREE.BufferGeometry();
  gridGeom.setAttribute("position", new THREE.Float32BufferAttribute(gridPositions, 3));
  group.add(new THREE.LineSegments(gridGeom, gridMat));

  const borderMat = new THREE.LineBasicMaterial({
    color: gridColor,
    transparent: true,
    opacity: 0.4,
    linewidth: 2,
  });
  const borderGeom = new THREE.BufferGeometry();
  borderGeom.setAttribute("position", new THREE.Float32BufferAttribute([
    -halfX, 0, -halfY,  halfX, 0, -halfY,
     halfX, 0, -halfY,  halfX, 0,  halfY,
     halfX, 0,  halfY, -halfX, 0,  halfY,
    -halfX, 0,  halfY, -halfX, 0, -halfY,
  ], 3));
  group.add(new THREE.LineSegments(borderGeom, borderMat));

  const axisLen = 30;
  const xGeom = new THREE.BufferGeometry();
  xGeom.setAttribute("position", new THREE.Float32BufferAttribute([
    -halfX, 0, -halfY, -halfX + axisLen, 0, -halfY
  ], 3));
  group.add(new THREE.LineSegments(xGeom, new THREE.LineBasicMaterial({ color: 0xff4444 })));

  const yGeom = new THREE.BufferGeometry();
  yGeom.setAttribute("position", new THREE.Float32BufferAttribute([
    -halfX, 0, -halfY, -halfX, 0, -halfY + axisLen
  ], 3));
  group.add(new THREE.LineSegments(yGeom, new THREE.LineBasicMaterial({ color: 0x44ff44 })));

  const zGeom = new THREE.BufferGeometry();
  zGeom.setAttribute("position", new THREE.Float32BufferAttribute([
    -halfX, 0, -halfY, -halfX, axisLen, -halfY
  ], 3));
  group.add(new THREE.LineSegments(zGeom, new THREE.LineBasicMaterial({ color: 0x4488ff })));

  return group;
}

/**
 * 가장 짧은 축이 높이(Y)가 되도록 자동 배치
 * 쿠키커터는 항상 납작한 형태 → 가장 짧은 축 = 높이
 */
function autoOrient(geometry) {
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return;

  const size = new THREE.Vector3();
  box.getSize(size);

  // 가장 짧은 축 찾기
  const dims = [
    { axis: "x", val: size.x },
    { axis: "y", val: size.y },
    { axis: "z", val: size.z },
  ];
  dims.sort((a, b) => a.val - b.val);
  const shortest = dims[0].axis;

  // 가장 짧은 축이 이미 Y면 회전 불필요
  if (shortest === "y") return;

  const rot = new THREE.Matrix4();
  if (shortest === "x") {
    // X가 가장 짧음 → X축을 Y축으로: Z축 기준 90° 회전
    rot.makeRotationZ(Math.PI / 2);
  } else if (shortest === "z") {
    // Z가 가장 짧음 → Z축을 Y축으로: X축 기준 -90° 회전
    rot.makeRotationX(-Math.PI / 2);
  }

  geometry.applyMatrix4(rot);
}
